import React, { useId, useMemo } from "react";
import { FiPlus } from "react-icons/fi";
import PhoneNumberInput from "./PhoneNumberInput";
import {
  MAX_MOBILE_NUMBERS,
  PHONE_VALIDATION,
  findDuplicatePhoneIndexes,
  isBlankPhoneValue,
  mapPhoneValidationMessage,
  validatePhoneNumber,
} from "../../utils/phoneNumberUtils";

const PhoneNumberFieldArray = ({
  values,
  onChange,
  disabled = false,
  hasError = false,
  addLabel,
  removeLabel,
  placeholder,
  hint,
  maxNumbers = MAX_MOBILE_NUMBERS,
  showFieldLabels = false,
  fieldLabelPrefix = "Mobile",
  t,
}) => {
  const hintId = useId();
  const numbers = values?.length ? values : [""];

  const duplicateIndexes = useMemo(() => findDuplicatePhoneIndexes(numbers), [numbers]);

  const updateAt = (index, nextValue) => {
    const next = [...numbers];
    next[index] = nextValue;
    onChange(next);
  };

  const removeAt = (index) => {
    const next = numbers.filter((_, idx) => idx !== index);
    onChange(next.length ? next : [""]);
  };

  const addNumber = () => {
    if (numbers.length >= maxNumbers) return;
    onChange([...numbers, ""]);
  };

  const canAddMore = numbers.length < maxNumbers;
  const lastNumberFilled = !isBlankPhoneValue(numbers[numbers.length - 1]);

  return (
    <div className="space-y-2">
      {hint ? (
        <p id={hintId} className="text-xs leading-relaxed text-slate-500">
          {hint}
        </p>
      ) : null}

      {numbers.map((number, index) => {
        const fieldId = `mobile-number-${index}`;
        const validationCode = validatePhoneNumber(number);
        const isDuplicate = duplicateIndexes.has(index);
        const fieldError =
          isDuplicate && t
            ? mapPhoneValidationMessage(PHONE_VALIDATION.DUPLICATE, t)
            : validationCode && validationCode !== PHONE_VALIDATION.EMPTY && number && t
              ? mapPhoneValidationMessage(validationCode, t)
              : null;
        const showFieldError = Boolean(fieldError) && (hasError || Boolean(number));

        return (
          <div key={fieldId} className="space-y-1">
            {showFieldLabels && numbers.length > 1 ? (
              <span className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
                {fieldLabelPrefix} {index + 1}
              </span>
            ) : null}
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <PhoneNumberInput
                  id={fieldId}
                  value={number}
                  onChange={(nextValue) => updateAt(index, nextValue)}
                  disabled={disabled}
                  hasError={hasError || showFieldError}
                  placeholder={placeholder}
                  describedBy={hint ? hintId : undefined}
                  inputProps={{
                    "aria-label":
                      numbers.length > 1 ? `${fieldLabelPrefix} ${index + 1}` : fieldLabelPrefix,
                  }}
                />
              </div>
              {numbers.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeAt(index)}
                  aria-label={`${removeLabel} ${index + 1}`}
                  disabled={disabled}
                  className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 transition hover:border-red-200 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                >
                  <span className="text-lg leading-none">−</span>
                </button>
              )}
            </div>
            {showFieldError ? (
              <p className="text-xs font-medium text-red-500" role="alert">
                {fieldError}
              </p>
            ) : null}
          </div>
        );
      })}

      <div className="flex flex-wrap items-center gap-3 pt-1">
        <button
          type="button"
          onClick={addNumber}
          disabled={disabled || !canAddMore || !lastNumberFilled}
          title={!canAddMore && t ? t("ccMobileNumberMax").replace("{max}", String(maxNumbers)) : undefined}
          className="inline-flex items-center gap-1 text-sm font-semibold text-[#2B7FFF] transition hover:text-[#1d4ed8] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <FiPlus className="h-4 w-4" />
          {addLabel}
        </button>
        {numbers.filter((value) => value).length > 0 ? (
          <span className="text-xs text-slate-400">
            {numbers.filter((value) => value).length}/{maxNumbers}
          </span>
        ) : null}
      </div>
    </div>
  );
};

export default PhoneNumberFieldArray;
